/**
 * Event codes emitted by desktop when Hilton shows wrong-password / change-password screens.
 * Older builds may only have PEP_CREDENTIAL_WALK_EXHAUSTED.
 */
export const PEP_HILTON_WRONG_PASSWORD_EVENT = 'PEP_HILTON_WRONG_PASSWORD'
export const PEP_HILTON_CHANGE_PASSWORD_EVENT = 'PEP_HILTON_CHANGE_PASSWORD'
export const PEP_CREDENTIAL_WALK_EXHAUSTED_EVENT = 'PEP_CREDENTIAL_WALK_EXHAUSTED'

function pickJsonField(json, camelKey, pascalKey) {
  if (!json || typeof json !== 'object') {
    return undefined
  }
  if (json[camelKey] !== undefined) {
    return json[camelKey]
  }
  return json[pascalKey]
}

function normalizeUsername(value) {
  return (value ?? '').toString().trim().toLowerCase()
}

function captureMatchesCredential(capture, credentialId, username) {
  const json = capture?.json
  const captureCredentialId = pickJsonField(json, 'credentialId', 'CredentialId')
  const captureUsername = normalizeUsername(
    pickJsonField(json, 'username', 'Username') ||
      pickJsonField(json, 'pepUserId', 'PepUserId')
  )

  if (credentialId && captureCredentialId != null && String(captureCredentialId) === credentialId) {
    return true
  }
  if (username && captureUsername && captureUsername === username) {
    return true
  }
  return false
}

/**
 * Finds diagnostic captures related to a stored PEP credential failure reason.
 * Prefers Hilton password event codes matched by credentialId/username; falls back to
 * nearby screenshots around LastFailureAt.
 *
 * @param {Array<object>} captures normalized diagnostic captures
 * @param {{ id?: number|string|null, username?: string|null, lastFailureAt?: string|null }} credential
 * @param {'wrongPassword' | 'changePassword' | null} [credentialIssue]
 * @returns {{ matches: Array<object>, emptyReason: string|null }}
 */
export function matchCredentialDiagnosticCaptures(captures, credential, credentialIssue) {
  const list = Array.isArray(captures) ? captures : []
  const credentialId =
    credential?.id !== null && credential?.id !== undefined
      ? String(credential.id)
      : null
  const username = normalizeUsername(credential?.username)

  const preferredCodes =
    credentialIssue === 'wrongPassword'
      ? new Set([PEP_HILTON_WRONG_PASSWORD_EVENT, PEP_CREDENTIAL_WALK_EXHAUSTED_EVENT])
      : credentialIssue === 'changePassword'
        ? new Set([PEP_HILTON_CHANGE_PASSWORD_EVENT, PEP_CREDENTIAL_WALK_EXHAUSTED_EVENT])
        : new Set([
            PEP_HILTON_WRONG_PASSWORD_EVENT,
            PEP_HILTON_CHANGE_PASSWORD_EVENT,
            PEP_CREDENTIAL_WALK_EXHAUSTED_EVENT
          ])

  const linked = list.filter(
    capture =>
      preferredCodes.has(capture?.eventCode) &&
      captureMatchesCredential(capture, credentialId, username)
  )

  const withScreenshot = captures => captures.filter(c => c.hasScreenshot && c.screenshotUrl)

  const linkedShots = withScreenshot(linked)
  if (linkedShots.length > 0) {
    return { matches: linkedShots, emptyReason: null }
  }
  if (linked.length > 0) {
    return {
      matches: linked,
      emptyReason: 'Related captures found but none include a screenshot yet.'
    }
  }

  const unlinkedPreferred = list.filter(capture => preferredCodes.has(capture?.eventCode))
  const unlinkedShots = withScreenshot(unlinkedPreferred)
  if (unlinkedShots.length > 0) {
    return { matches: unlinkedShots, emptyReason: null }
  }

  const failureAtMs = credential?.lastFailureAt
    ? Date.parse(credential.lastFailureAt)
    : NaN
  if (Number.isFinite(failureAtMs)) {
    const windowMs = 6 * 60 * 60 * 1000
    const nearby = list.filter(capture => {
      if (!capture?.hasScreenshot || !capture?.screenshotUrl || !capture?.occurredAt) {
        return false
      }
      const at = Date.parse(capture.occurredAt)
      return Number.isFinite(at) && Math.abs(at - failureAtMs) <= windowMs
    })
    if (nearby.length > 0) {
      return { matches: nearby, emptyReason: null }
    }
  }

  return {
    matches: [],
    emptyReason:
      'No screenshot captured yet (requires updated desktop app after the password failure).'
  }
}
